import Analysis from "api/lib/data/Analysis";
import QueryObject from "api/lib/data/interfaces/QueryObject";
import * as React from "react";
import * as _ from "lodash";
import Tabs from "react-draggable-tabs";
import { Icon, Input, List, Popup } from "semantic-ui-react";
import { cloneWithOutReference } from "src/utils";

import "../../query-tabs.css";
import { Base, IBaseProps, IBaseState, Event } from "../Common/Base";
import QueryTab from "../Query/QueryTab";
import { dbConstants } from "api/lib/data/firebase";
import Query from "api/lib/data/Query";
import { UsersStatus } from "../Session/withAuthorization";
import * as routes from "../../constants/routes";

export interface QueryTabsProps extends IBaseProps {
  orgId: string;
  authUser: any;
  analysisId: string;
  queryId: string;
  usersStatus: UsersStatus;
}

export interface QueryTabsState extends IBaseState {
  analysis: Analysis;
  tabs: any[];
  allTabs: any[];
  filteredTabs: any[];
  closingTab: boolean;
}

export default class QueryTabs extends Base<QueryTabsProps, QueryTabsState> {
  constructor(props: QueryTabsProps) {
    super(props);

    this.state = {
      ...Base.BASE_INITAL_STATE,
      analysis: null,
      tabs: [],
      allTabs: [],
      filteredTabs: [],
      closingTab: false,
    };
  }
  private moveTab = (dragIndex, hoverIndex) => {
    const newTabs = [...this.state.tabs];
    newTabs.splice(hoverIndex, 0, newTabs.splice(dragIndex, 1)[0]);
    this.updateTabs(newTabs);
  };

  private selectTab = (selectedIndex, selectedID) => {
    const { tabs } = this.state;
    const selectedTab = tabs.find((tab) => tab.id === selectedID);
    if (selectedTab && !selectedTab.active) {
      const newTabs = tabs.map((tab) => ({
        ...tab,
        active: tab.id === selectedID,
        openedAt:
          tab.id === selectedID
            ? dbConstants.ServerValue.TIMESTAMP
            : tab.openedAt,
      }));
      this.updateQueryUrl(selectedID);
      this.updateTabs(newTabs);
    }
  };

  private updateQueryUrl = (queryId: string) => {
    const { history, orgId, analysisId } = this.props;
    if (queryId) {
      history.push(routes.QUERY_BY_ID(orgId, analysisId, queryId));
    }
  };

  private handleDeleteQuery = (queryId) => {
    const { tabs } = this.state;
    const removedIndex = _.findIndex(tabs, (t) => t.id === queryId);
    this.closedTab(removedIndex, queryId);
  };

  private closedTab = (removedIndex, removedID) => {
    const newTabs = cloneWithOutReference(this.state.tabs);
    newTabs.splice(removedIndex, 1);

    if (this.state.tabs[removedIndex].active && newTabs.length !== 0) {
      // automatically select another tab if needed
      const newActive = removedIndex === 0 ? 0 : removedIndex - 1;
      newTabs[newActive].active = true;
      this.updateQueryUrl(newTabs[newActive].id);
      const q = _.find(this.state.allTabs, (t) => t.id === removedID);
      if (q && !q.datasourceId) {
        this.removeQuery(removedID);
      }
    }
    this.setState({ closingTab: true }, () => {
      this.updateTabs(newTabs);
    });
  };

  private removeQuery = (queryId: string) => {
    const { authUser, orgId, analysisId } = this.props;
    const query = new Query(orgId, analysisId, queryId);
    query
      .requestDeleteQuery(authUser.uid)
      .then((requestRef: firebase.database.Reference) => {
        this.getListner(
          query.getApiResponse(orgId, authUser.uid, requestRef.key),
          Event.Value,
          (snapshot: firebase.database.DataSnapshot) => {
            const resp = snapshot.val();
            if (resp) {
              // TODO
            }
          }
        );
      });
  };

  private addTab = () => {
    // check if any newQuery tab is present and select the tab
    const activeTabs = this.state.tabs;
    const newTab = _.find(activeTabs, (at) => !at.datasourceId);
    if (newTab) {
      this.selectTab(0, newTab.id);
    } else {
      const qo: QueryObject = {
        config: {
          name: "New Query",
        },
        audit: {
          createdBy: this.props.authUser.uid,
        },
      };
      this.state.analysis
        .createQuery(qo)
        .then((requestRef: firebase.database.Reference) => {
          const newQId = requestRef.key;
          const tabs = this.state.tabs;
          const newTabs = tabs.map((tab) => {
            return { ...tab, active: false };
          });
          newTabs.push({
            id: newQId,
            content: "New Query",
            active: true,
            openedAt: dbConstants.ServerValue.TIMESTAMP,
            display: (
              <QueryTab
                key={newQId}
                {...this.props}
                queryId={newQId}
                onRemove={this.handleDeleteQuery}
              />
            ),
          });
          this.updateQueryUrl(newQId);
          this.updateTabs(newTabs);
        });
    }
  };

  private tabAlreadyExists = (tab: any) => {
    const { tabs } = this.state;
    const found = _.find(tabs, (t) => t.id === tab.id);
    return found;
  };

  private addExistingTab = (tab: any) => {
    const { tabs } = this.state;
    const existingTab = this.tabAlreadyExists(tab);
    if (existingTab) {
      const newTabs = tabs.map((t) => {
        const nt = { ...t };
        nt.active = t.id === tab.id;
        return nt;
      });
      this.setState({ tabs: newTabs });
    } else {
      const newTabs = tabs.map((t) => {
        return { ...t, active: false };
      });
      newTabs.push({
        id: tab.id,
        content: tab.name,
        active: true,
        openedAt: dbConstants.ServerValue.TIMESTAMP,
        display: (
          <QueryTab
            key={tab.id}
            {...this.props}
            queryId={tab.id}
            onRemove={this.handleDeleteQuery}
          />
        ),
      });
      this.updateTabs(newTabs);
    }

    this.updateQueryUrl(tab.id);
  };

  private isTabActive = (tab: any) => {
    const { queryId } = this.props;
    const { closingTab } = this.state;
    if (queryId && !closingTab) {
      return queryId === tab.id;
    } else {
      return tab.active;
    }
  };

  private getData = () => {
    const { orgId, analysisId, authUser } = this.props;
    const analysis = new Analysis(orgId, analysisId);
    this.setState({ analysis });
    this.getListner(
      analysis.getQueryTabs(authUser.uid),
      Event.Value,
      (snapshot: firebase.database.DataSnapshot) => {
        // const { closingTab } = this.state;
        const queryTabs = snapshot.val();
        if (queryTabs) {
          const promises = [];

          queryTabs.forEach((tab: any, index: number) => {
            const query = new Query(orgId, analysisId, tab.id);
            promises.push(
              this.getListner(
                query.getConfig(tab.id),
                Event.Value,
                (qConfigsnapshot: firebase.database.DataSnapshot) => {
                  const qConfig = qConfigsnapshot.val();
                  if (qConfig) {
                    const { tabs } = this.state;
                    tabs[index] = {
                      id: tab.id,
                      content: qConfig.name,
                      datasourceId: qConfig.datasourceId,
                      active: this.isTabActive(tab),
                      openedAt: tab.openedAt,
                      display: (
                        <QueryTab
                          {...this.props}
                          key={tab.id}
                          queryId={tab.id}
                          onRemove={this.handleDeleteQuery}
                        />
                      ),
                    };
                    this.setState({ tabs });
                  }
                }
              )
            );
          });
          Promise.all(promises).then((values) => {
            // if (tabs.length === 0 && !closingTab) {
            //   this.addTab();
            // }

            this.setState({ closingTab: false });
          });
        } else {
          this.setState({ tabs: [] });
          if (!this.state.closingTab) {
            this.addTab();
          }
        }
      }
    );
    this.getListner(
      analysis.getQueries(),
      Event.Value,
      (snapshot: firebase.database.DataSnapshot) => {
        const dataObj = snapshot.val();
        if (dataObj) {
          const thisAllTabs = Object.keys(dataObj).map((item) => {
            const q = dataObj[item];
            return {
              id: item,
              name: q.config.name,
              datasourceId: q.config.datasourceId,
            };
          });
          this.setState({ allTabs: thisAllTabs }, () => {
            this.getFilteredData("");
            this.openQueryFromURL(thisAllTabs);
          });
        } else {
          this.setState({ allTabs: [] }, () => {
            this.getFilteredData("");
          });
        }
      }
    );
  };

  private openQueryFromURL = (thisAllTabs: any[]) => {
    const { queryId } = this.props;
    if (queryId) {
      const newTab = _.find(thisAllTabs, (t) => t.id === queryId);
      if (newTab) {
        this.addExistingTab(newTab);
      }
    }
  };

  private updateTabs = (tabs: any[]) => {
    const { orgId, analysisId, authUser } = this.props;
    const analysis = new Analysis(orgId, analysisId);
    const updatedTabs = tabs.map((tab) => {
      return {
        id: tab.id,
        active: tab.active,
        openedAt: tab.openedAt ? tab.openedAt : "",
      };
    });
    analysis
      .updateQueryTabs(authUser.uid, updatedTabs)
      .then((ref: firebase.database.Reference) => {
        // TODO
      })
      .catch((error: Error) => {
        console.error(error);
      });
  };

  private handleSearchInputChange = (
    e: React.SyntheticEvent,
    { value }: any
  ) => {
    this.getFilteredData(value);
  };

  private getFilteredData = (searchString: string) => {
    const { allTabs } = this.state;
    let filteredData = [];
    if (_.isEmpty(searchString)) {
      filteredData = allTabs;
    } else {
      filteredData = _.filter(allTabs, (t) =>
        _.toLower(t.name).includes(_.toLower(searchString))
      );
    }
    this.setState({ filteredTabs: filteredData });
  };

  private renderAllQueries = () => {
    const { filteredTabs } = this.state;
    return (
      <React.Fragment>
        <Input
          icon="search"
          iconPosition="left"
          name="search"
          // focus={true}
          onClick={(e: React.BaseSyntheticEvent) => e.stopPropagation()}
          onChange={this.handleSearchInputChange}
          placeholder="Search Queries"
          style={{ width: "100%", padding: "10px 0" }}
        />
        <List
          verticalAlign="middle"
          className="schema-browser"
          style={{
            height: "300px",
            width: "200px",
          }}
          selection={true}
        >
          {filteredTabs.map((tab) => {
            return (
              <List.Item
                key={tab.id}
                onClick={() => {
                  this.addExistingTab(tab);
                }}
              >
                <List.Content>{tab.name}</List.Content>
              </List.Item>
            );
          })}
        </List>
      </React.Fragment>
    );
  };

  public componentDidMount = () => {
    this.getData();
  };

  public componentDidUpdate = (prevProps, prevState) => {
    const { usersStatus, analysisId, queryId } = this.props;
    if (
      JSON.stringify(usersStatus) !== JSON.stringify(prevProps.usersStatus) ||
      analysisId !== prevProps.analysisId
    ) {
      this.removeListeners();
      this.getData();
    }

    if (queryId !== prevProps.queryId) {
      this.selectTab(0, queryId);
    }
  };

  public render = () => {
    const { tabs } = this.state;
    let activeTab;
    if (tabs && tabs.length > 0) {
      activeTab = _.find(tabs, (t) => t && t.active);
    }

    return (
      <div>
        <style
          dangerouslySetInnerHTML={{
            __html: `
            .react-tabs-container {
              background-color: #fff;
              border-color: #fff;
              border-bottom: 0px solid rgba(34, 36, 38, 0.15);
            }
            .react-tabs-tab {
              color: rgba(0, 0, 0, 0.7);
              cursor: pointer;
              border-bottom: 2px solid transparent;
            }
            .react-tabs-tab:hover {
              border-bottom: 2px solid #4e78f4;
            }
            
            .react-tabs-active {
              color: #4e78f4;
              background-color: #fff;
              border-left: none;
              border-bottom: 2px solid #4e78f4;
            }
            
            .react-tabs-tab-close {
              border-color: #fff;
              border: none;
              position: relative;
              top: -3px;
            }
            .react-tabs-tab-close:hover {
              background-color: #fff;
              color: rgba(0, 0, 0, 0.87);
              border-color: #fff;
            }
            .react-tabs-active .react-tabs-tab-close:hover {
              background-color: #fff;
              color: rgba(0, 0, 0, 0.87);
              border-color: #fff;
            }
            .ui.segment.QueryTab__header {
              background: #ffffff;
              font-size: 0.9rem;
            }
            .react-tabs-child {
              margin-left: 0;
            }
            
    `,
          }}
        />
        <Tabs
          moveTab={this.moveTab}
          selectTab={this.selectTab}
          closeTab={this.closedTab}
          tabs={tabs}
        >
          <Popup
            content={this.renderAllQueries()}
            position="bottom left"
            on="click"
            trigger={<Icon link={true} name="angle down" />}
          />
          <Popup
            content="Create new query"
            position="bottom left"
            trigger={<Icon link={true} name="add" onClick={this.addTab} />}
          />
        </Tabs>
        {activeTab ? activeTab.display : ""}
      </div>
    );
  };
}



// WEBPACK FOOTER //
// ./src/components/QueryTabs/index.tsx